import { DeleteOutlined, LinkOutlined, PlusOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  Row,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import { debounce } from "lodash";
import React from "react";
import { useNavigate } from "react-router-dom";
import ProjectOverview from "../../components/research_development/ProjectOverview";
import { ProjectStatus } from "../../enum/common";
import { IPaginatedResponse } from "../../models/Pagination";
import {
  ICreateResearchProject,
  IFilterResearch,
  IResearchProject,
} from "../../models/Research";
import {
  useCreateResearchProject,
  useGetAllResearchProject,
} from "../../query/ReseachQuery";
import { notify } from "../../utils/toast";
import ProjectRemindPhase from "../../components/research_development/ProjectRemindPhase";

const ProjectList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [params, setParams] = React.useState<IFilterResearch>({
    page: 1,
    limit: 10,
  });

  const [openDrawer, setOpenDrawer] = React.useState<boolean>(false);
  const [items, setItems] = React.useState<
    Array<{ id: string; name: string; cost: string }>
  >([]);
  const [totalBudget, setTotalBudget] = React.useState<string>("");

  const { data: projectsInfo, isLoading } = useGetAllResearchProject(params);

  const createResearchProject = useCreateResearchProject();
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data: ICreateResearchProject = {
        ...values,
        budgetCategories: items.map((item) => ({
          categoryName: item.name,
          budgetedAmount: item.cost,
        })),
        totalBudget: parseFloat(totalBudget),
        requestDate: values.requestDate.format("YYYY-MM-DD"),
        startDate: values.startDate.format("YYYY-MM-DD"),
        deadline: values.deadline.format("YYYY-MM-DD"),
      };
      await createResearchProject.mutateAsync(data, {
        onSuccess: (newProject: IResearchProject) => {
          setOpenDrawer(false);
          form.resetFields();
          setItems([]);
          setTotalBudget("");
          queryClient.setQueryData(
            ["research-project", params],
            (oldData: IPaginatedResponse<IResearchProject>) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                data: [newProject, ...oldData.data],
                pagination: {
                  ...oldData.pagination,
                  total: (oldData.pagination.total || 0) + 1,
                },
              };
            },
          );
          notify.success("Tạo dự án thành công");
        },
      });
    } catch (error) {
      notify.error("Có lỗi xảy ra");
    }
  };
  const addItem = () => {
    const newItem = {
      id: Date.now().toString(),
      name: "",
      cost: "",
    };
    setItems([...items, newItem]);
  };

  const removeItem = (id: string) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const updateItem = (id: string, field: string, value: string) => {
    setItems(
      items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const calculateRemaining = (totalBudget: string) => {
    const total = parseFloat(totalBudget) || 0;
    const itemsTotal = items.reduce((sum, item) => {
      return sum + (parseFloat(item.cost) || 0);
    }, 0);
    return Math.max(0, total - itemsTotal);
  };

  const isItemsExceedBudget = (totalBudget: string) => {
    const total = parseFloat(totalBudget) || 0;
    const itemsTotal = items.reduce((sum, item) => {
      return sum + (parseFloat(item.cost) || 0);
    }, 0);
    return itemsTotal > total && total > 0;
  };
  const debouncedSearch = React.useMemo(
    () =>
      debounce((field: keyof IFilterResearch, value: string) => {
        setParams((prev) => ({
          ...prev,
          [field]: value || undefined,
          page: 1,
        }));
      }, 300),
    [],
  );

  const handleSearch = (field: keyof IFilterResearch) => (value: string) => {
    debouncedSearch(field, value);
  };

  const handleStatusClick = (status: string) => {
    if (status === "") {
      setParams((prev) => ({
        ...prev,
        status: undefined,
        page: 1,
      }));
      return;
    }
    setParams((prev) => ({
      ...prev,
      status: status,
      page: 1,
    }));
  };

  const column: TableColumnsType<IResearchProject> = [
    {
      title: "Tên dự án",
      dataIndex: "projectName",
      key: "projectName",
      align: "center",
      render: (text) => (
        <>
          <Typography.Text className="font-bold">{text}</Typography.Text>
        </>
      ),
      filterDropdown: () => {
        return (
          <div className="p-4">
            <Input
              placeholder="Nhập tên dự án"
              onChange={(e) => {
                handleSearch("projectName")(e.target.value);
              }}
            />
          </div>
        );
      },
    },

    {
      title: "Dự án",
      dataIndex: "project",
      key: "project",
      align: "center",
      filterDropdown: () => {
        return (
          <div className="p-4">
            <Input
              placeholder="Nhập mã dự án"
              onChange={(e) => {
                handleSearch("project")(e.target.value);
              }}
            />
          </div>
        );
      },
    },
    {
      title: "Khách hàng",
      dataIndex: "customerName",
      key: "customerName",
      align: "center",
    },

    {
      title: "Yêu cầu từ",
      dataIndex: "requestFrom",
      key: "requestFrom",
      align: "center",
    },
    {
      title: "Ngày yêu cầu",
      dataIndex: "requestDate",
      key: "requestDate",
      align: "center",
    },
    {
      title: "Ngày khởi chạy",
      dataIndex: "startDate",
      key: "startDate",
      align: "center",
      render: (text) => (
        <>
          <Tag color="green">{text}</Tag>
        </>
      ),
    },
    {
      title: "Ngày hoàn thành",
      dataIndex: "deadline",
      key: "deadline",
      align: "center",
      render: (text) => (
        <>
          <Tag color="red">{text}</Tag>
        </>
      ),
    },
    {
      title: "Trạng thái dự án",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (text) => {
        let color = "default";
        let content = "";
        switch (text) {
          case ProjectStatus.NEW:
            color = "blue";
            content = "Mới";
            break;
          case ProjectStatus.IN_PROGRESS:
            color = "orange";
            content = "Đang thực hiện";
            break;
          case ProjectStatus.DONE:
            color = "green";
            content = "Hoàn thành";
            break;
          case ProjectStatus.CANCELLED:
            color = "red";
            content = "Đã hủy";
            break;
          default:
            color = "default";
            content = text;
            break;
        }
        return <Tag color={color}>{content}</Tag>;
      },
    },

    {
      title: "Hành động",
      dataIndex: "action",
      key: "action",
      align: "center",
      render: (_, record) => {
        return (
          <Space>
            <Button
              type="link"
              onClick={() => navigate(`${record.alias}`)}
              icon={<LinkOutlined />}
            >
              Chi tiết
            </Button>
          </Space>
        );
      },
    },
  ];
  return (
    <div className="p-4">
      <div className="flex justify-end mb-4">
        <Button
          style={{ backgroundColor: "green" }}
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setOpenDrawer(true);
          }}
        >
          Tạo dự án mới
        </Button>
      </div>
      <ProjectOverview onStatusClick={handleStatusClick} />
      <Row className="mt-4" gutter={[16, 16]}>
        <Col xs={24} md={24} lg={18}>
          <Table
            size="small"
            columns={column}
            dataSource={projectsInfo?.data}
            rowKey={(record) => record.id.toString()}
            loading={isLoading}
            scroll={{ x: 1000 }}
            pagination={{
              current: params.page,
              pageSize: params.limit,
              total: projectsInfo?.pagination.total,
              onChange: (page, pageSize) => {
                setParams({
                  ...params,
                  page,
                  limit: pageSize,
                });
              },
            }}
          />
        </Col>

        <Col xs={24} md={24} lg={6} className="border-l pl-4">
          <ProjectRemindPhase />
        </Col>
      </Row>
      <Drawer
        title="Tạo dự án mới"
        placement="right"
        onClose={() => setOpenDrawer(false)}
        open={openDrawer}
        width={1200}
        extra={
          <Space>
            <Button
              type="primary"
              onClick={() => {
                handleSave();
              }}
              loading={createResearchProject.isPending}
            >
              Lưu
            </Button>
          </Space>
        }
      >
        <Form layout="vertical" form={form}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Tên dự án"
                name="projectName"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng nhập tên dự án",
                  },
                ]}
              >
                <Input placeholder="Nhập tên dự án" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Dự án"
                name="project"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng nhập dự án",
                  },
                ]}
              >
                <Input placeholder="Nhập dự án" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Mô tả dự án"
                name="projectDescription"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng nhập mô tả dự án",
                  },
                ]}
              >
                <Input placeholder="Nhập mô tả dự án" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Yêu cầu từ"
                name="requestFrom"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng nhập nơi nhận yêu cầu",
                  },
                ]}
              >
                <Input placeholder="Nhập yêu cầu từ" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label="Khách hàng"
                name="customerName"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng nhập khách hàng",
                  },
                ]}
              >
                <Input placeholder="Nhập khách hàng" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Liên hệ"
                name="contactProject"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng nhập liên hệ",
                  },
                ]}
              >
                <Input placeholder="Nhập liên hệ" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label="Sale phụ trách" name="saleInCharge">
                <Input placeholder="Nhập sale phụ trách" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Mua hàng phụ trách" name="purchaseInCharge">
                <Input placeholder="Nhập mua hàng phụ trách" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Sale Admin phụ trách" name="saleAdInCharge">
                <Input placeholder="Nhập sale admin phụ trách" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Ngày yêu cầu"
                name="requestDate"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng chọn ngày yêu cầu",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Chọn ngày yêu cầu"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Ngày khởi chạy"
                name="startDate"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng ngày khởi chạy",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Chọn ngày khởi chạy"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Ngày hoàn thành"
                name="deadline"
                rules={[
                  {
                    required: true,
                    message: "Vui lòng ngày hoàn thành",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Chọn ngày hoàn thành"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">Chi phí</Divider>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Tổng chi phí"
                name="totalBudget"
                validateStatus={isItemsExceedBudget(totalBudget) ? "error" : ""}
                help={
                  isItemsExceedBudget(totalBudget)
                    ? "Tổng hạng mục vượt quá tổng chi phí"
                    : ""
                }
                rules={[
                  {
                    required: true,
                    message: "Vui lòng nhập tổng chi phí dự toán",
                  },
                ]}
              >
                <Input
                  placeholder="Nhập tổng chi phí"
                  type="number"
                  value={totalBudget}
                  onChange={(e) => setTotalBudget(e.target.value)}
                />
              </Form.Item>
              <Typography.Text
                style={{
                  color:
                    calculateRemaining(totalBudget) === 0 ? "green" : "red",
                }}
              >
                {`Số tiền còn lại: ${calculateRemaining(totalBudget).toFixed(
                  2,
                )}`}
              </Typography.Text>
            </Col>
          </Row>

          <div className="flex justify-end mb-3">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              onClick={addItem}
            >
              Thêm
            </Button>
          </div>

          <Table
            size="small"
            columns={[
              {
                title: "Tên hạng mục",
                dataIndex: "name",
                key: "name",
                render: (text, record) => (
                  <Input
                    size="small"
                    placeholder="Nhập tên hạng mục"
                    value={record.name}
                    onChange={(e) =>
                      updateItem(record.id, "name", e.target.value)
                    }
                  />
                ),
              },
              {
                title: "Chi phí",
                dataIndex: "cost",
                key: "cost",
                width: 120,
                render: (text, record) => (
                  <Input
                    size="small"
                    type="number"
                    placeholder="0"
                    value={record.cost}
                    onChange={(e) =>
                      updateItem(record.id, "cost", e.target.value)
                    }
                  />
                ),
              },
              {
                title: "",
                key: "action",
                width: 50,
                align: "center" as const,
                render: (_, record) => (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeItem(record.id)}
                  />
                ),
              },
            ]}
            dataSource={items}
            pagination={false}
            locale={{
              emptyText: "Chưa có hạng mục nào",
            }}
          />
        </Form>
      </Drawer>
    </div>
  );
};

export default ProjectList;
